import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { useWavesurfer } from '@wavesurfer/react';
import type WaveSurfer from 'wavesurfer.js';
import Hover from 'wavesurfer.js/dist/plugins/hover.esm.js';
import { useTheme } from '@mui/material';
import { useFileDownloadMutation } from '@/generated/graphql';

type WavesurferOptions = Omit<
  Parameters<typeof useWavesurfer>[0],
  'container' | 'url'
>;

interface UseLazyWavesurferArgs {
  projectId?: string;
  resolveAudioSource?: () => Promise<string | null>;
  fileId: string;
  containerRef: RefObject<HTMLDivElement | null>;
  /** Visual config (height, colors, peaks, plugins...). `url` is managed here. */
  options: WavesurferOptions;
  /** Runs when the audio finishes loading, before any autoplay. */
  onAudioReady?: (wavesurfer: WaveSurfer) => void;
}

/**
 * Wavesurfer that renders from peaks immediately and only fetches the
 * presigned audio URL on the first play; playback starts automatically once
 * the audio is ready. Shared by AudioPlayer and ReviewWaveform.
 */
export function useLazyWavesurfer({
  projectId,
  resolveAudioSource,
  fileId,
  containerRef,
  options,
  onAudioReady,
}: UseLazyWavesurferArgs) {
  const [fileDownload] = useFileDownloadMutation();
  const [audioSourceURL, setAudioSourceURL] = useState<string>('');
  const [isLoadingAudioSource, setIsLoadingAudioSource] = useState(false);
  // Bumped on unmount and whenever the file changes so a slow presigned-URL
  // fetch can never apply its result to a different file (or after unmount).
  const requestIdRef = useRef(0);

  const theme = useTheme();
  // Shared hover cursor/label styling; callers may override via options.plugins.
  const defaultPlugins = useMemo(
    () => [
      Hover.create({
        lineColor: theme.palette.primary.light,
        lineWidth: 1,
        labelBackground: theme.palette.background.paper,
        labelColor: theme.palette.primary.main,
        labelSize: '10px',
      }),
    ],
    [theme]
  );

  const { wavesurfer, isPlaying, currentTime } = useWavesurfer({
    plugins: defaultPlugins,
    ...options,
    container: containerRef,
    // Load explicitly after event listeners are attached.
    // Changing the source must not recreate the WaveSurfer instance.
  });

  const shouldAutoPlayRef = useRef(false);
  const readyRef = useRef(false);

  const invalidateInFlightFetch = useCallback(() => {
    requestIdRef.current++;
  }, []);

  useEffect(() => {
    return () => {
      invalidateInFlightFetch(); // drops any fetch started for this fileId
      setAudioSourceURL('');
      setIsLoadingAudioSource(false);
      shouldAutoPlayRef.current = false;
      readyRef.current = false;
    };
  }, [projectId, fileId, resolveAudioSource, invalidateInFlightFetch]);

  const onAudioReadyRef = useRef(onAudioReady);
  useEffect(() => {
    onAudioReadyRef.current = onAudioReady;
  }, [onAudioReady]);

  const getAudioSourceURL = useCallback(async (): Promise<string | null> => {
    if (resolveAudioSource) return resolveAudioSource();
    if (!projectId) return null;
    const result = await fileDownload({
      variables: { data: { projectId, fileId } },
    });
    return result.data?.fileDownload?.presignedUrl ?? null;
  }, [fileDownload, projectId, fileId, resolveAudioSource]);

  // Toggles when audio is loaded, otherwise fetches the source; the ready
  // effect below then starts playback.
  const onPlayPause = useCallback(async () => {
    if (!wavesurfer || isLoadingAudioSource) return;
    if (audioSourceURL) {
      wavesurfer.playPause();
      return;
    }
    const requestId = requestIdRef.current;
    setIsLoadingAudioSource(true);
    shouldAutoPlayRef.current = true;
    try {
      const url = await getAudioSourceURL();
      if (requestId !== requestIdRef.current) return; // unmounted or file changed
      if (!url) {
        shouldAutoPlayRef.current = false;
        setIsLoadingAudioSource(false);
      } else {
        setAudioSourceURL(url);
      }
    } catch {
      if (requestId === requestIdRef.current) {
        shouldAutoPlayRef.current = false;
        setIsLoadingAudioSource(false);
      }
    }
  }, [wavesurfer, isLoadingAudioSource, audioSourceURL, getAudioSourceURL]);

  /** Drops a pending source fetch / autoplay so nothing starts afterwards. */
  const cancelAutoPlay = useCallback(() => {
    invalidateInFlightFetch();
    shouldAutoPlayRef.current = false;
    setIsLoadingAudioSource(false);
  }, [invalidateInFlightFetch]);

  useEffect(() => {
    if (!wavesurfer || !audioSourceURL) return;
    readyRef.current = false;
    let active = true;
    const media = wavesurfer.getMediaElement();
    const clearLoading = () => {
      if (!active) return;
      shouldAutoPlayRef.current = false;
      setIsLoadingAudioSource(false);
    };
    const onError = () => {
      if (!active) return;
      clearLoading();
      readyRef.current = false;
      // Let the next play request refresh an expired or failed signed URL.
      setAudioSourceURL('');
    };
    const onReady = () => {
      if (!active) return;
      readyRef.current = true;
      onAudioReadyRef.current?.(wavesurfer);
      if (shouldAutoPlayRef.current) {
        // The play event means playback was requested, not that audio is audible.
        void wavesurfer.play().catch(clearLoading);
      } else {
        clearLoading();
      }
    };
    wavesurfer.once('ready', onReady);
    wavesurfer.once('error', onError);
    media.addEventListener('playing', clearLoading);
    media.addEventListener('error', onError);
    // Subscribe first: cached media can become ready during load().
    void wavesurfer
      .load(audioSourceURL, options.peaks, options.duration)
      .catch(onError);
    return () => {
      active = false;
      wavesurfer.pause();
      wavesurfer.un('ready', onReady);
      wavesurfer.un('error', onError);
      media.removeEventListener('playing', clearLoading);
      media.removeEventListener('error', onError);
    };
  }, [wavesurfer, audioSourceURL, options.peaks, options.duration]);

  return {
    wavesurfer,
    isPlaying,
    currentTime,
    audioSourceURL,
    isLoadingAudioSource,
    /** True once the lazily loaded audio has fired 'ready'. */
    readyRef,
    onPlayPause,
    cancelAutoPlay,
  };
}
